// SPDX-License-Identifier: AGPL-3.0-or-later
// LOCAL-ONLY: the fork's message menu layout (right-click menu and the mobile-web long-press sheet); a fork-only
// file — keep on upstream sync.

import type {messageActionMenuItemIds} from '@app/features/channel/components/MessageActionMenu';
import type {RELOCATE_MESSAGE_MENU_ITEM_ID} from '@app/features/channel/utils/RelocateMenuEntry';

type MessageActionMenuItemId =
	| (typeof messageActionMenuItemIds)[keyof typeof messageActionMenuItemIds]
	| typeof RELOCATE_MESSAGE_MENU_ITEM_ID;

/**
 * Entries hidden from the message menu, display only: their handlers, keyboard shortcuts and other entry points
 * (the hover toolbar and its reaction button) are untouched. `translate` is the browser's Translate entries,
 * which the right-click menu renders on its own rather than as a menu item.
 */
export const HIDDEN_MESSAGE_MENU_ITEM_IDS = [
	'add-reaction',
	'message_speak',
	'message_mark_unread',
] as const satisfies ReadonlyArray<MessageActionMenuItemId>;
export const SHOW_TRANSLATE_IN_MESSAGE_MENU = false;

/**
 * The fork's order, one array per group (a separator falls between groups). "Relocate messages" has its own group
 * right after the Cast group (relocation parity, 2026-10-30); without Cast items it comes where they would be.
 */
export const MESSAGE_MENU_GROUP_ORDER = [
	['edit', 'reply', 'forward', 'copy_message'],
	['message_toggle_ic', 'message_manage_characters'],
	['message_relocate'],
	['message_delete', 'message_pin', 'message_bookmark', 'message_copy_link', 'message_copy_id'],
] as const satisfies ReadonlyArray<ReadonlyArray<MessageActionMenuItemId>>;

const HIDDEN = new Set<string>(HIDDEN_MESSAGE_MENU_ITEM_IDS);
const ORDERED = new Set<string>(MESSAGE_MENU_GROUP_ORDER.flat());

/** Menu sheet items are a union (sliders and checkboxes too), so the id is read structurally. */
function itemId(item: unknown): string | undefined {
	const id = typeof item === 'object' && item !== null ? (item as {id?: unknown}).id : undefined;
	return typeof id === 'string' ? id : undefined;
}

/**
 * Lays out a sent message's menu items: the ordered groups first, then every remaining item that is neither
 * ordered nor hidden in one last group, in the order the menu data built them. Empty groups are dropped.
 */
export function layoutMessageMenuItems<T>(groups: ReadonlyArray<{items: ReadonlyArray<T>}>): Array<Array<T>> {
	const items = groups.flatMap((group) => group.items);
	const byId = new Map<string, T>();
	for (const item of items) {
		const id = itemId(item);
		if (id && !byId.has(id)) {
			byId.set(id, item);
		}
	}
	const ordered = MESSAGE_MENU_GROUP_ORDER.map((ids) =>
		ids.flatMap((id) => {
			const item = byId.get(id);
			return item ? [item] : [];
		}),
	);
	const rest = items.filter((item) => {
		const id = itemId(item);
		return !(id && (HIDDEN.has(id) || ORDERED.has(id)));
	});
	return [...ordered, rest].filter((group) => group.length > 0);
}
